// Évaluations annuelles — staff screen (soft-island port of the
// design Évaluations.html prototype, ev-* files). Manager side only:
// campagne (cockpit), équipe, objectifs + the per-employee panel
// (évaluer / auto-éval / comparaison / 1:1 & consensus / objectifs).
//
// Deviations from the prototype (deliberate):
// - the proto's own topbar/persona switcher/notification bell/Tweaks
//   panel are dropped — the app shell owns the chrome, and the
//   employee persona lives in the real Espace employé (/espace),
// - demo dataset replaced by /api/evaluations; drafts autosave,
// - the reminder emails really go out (Gmail) and are audit-logged,
// - confidential: BOSS/ADMIN only (evaluations.view, like payroll).
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import {
  EVAL_PHASES, evalCycle,
  type EvalEmployeeDTO, type EvalNotes, type EvalSlot,
} from '@pointage/shared';
import {
  useCloseEvalSession, useCreateObjective, useEvaluations, useMe, useObjectiveAction,
  useProposeOtoSlots, useRemindEval, useSaveMgrEval, useSignEvalMgr, useSubmitMgrEval,
} from '../api/hooks.js';
import { useUiStore } from '../store/ui.js';
import { toast } from '../store/toast.js';
import { cgToday } from '../components/conges/common.js';
import { EvEmpCell, EvEvalForm, EvStPill, evFmtYmd } from '../components/evaluations/common.js';
import { EvGapView, EvOtoView, EvFiche } from '../components/evaluations/GapOto.js';
import { EvObjSection, EvObjBoard, type ObjCheckInput } from '../components/evaluations/Objectifs.js';
import { EvCampagne, EvEquipe, EvEmailModal, type EvRow } from '../components/evaluations/Cockpit.js';
import '../styles/conges.css';
import '../styles/evaluations.css';

type View = 'campagne' | 'equipe' | 'objectifs';

function EvConfetti() {
  const cs = ['#7037ef', '#0bb5ab', '#ff5e11', '#d24df3', '#0a9d72'];
  return (
    <div className="ev-conf">
      {Array.from({ length: 36 }).map((_, i) => (
        <i key={i} style={{ left: `${Math.random() * 100}%`, background: cs[i % 5], animationDelay: `${Math.random() * 0.5}s`, transform: 'translateY(-10vh)' }} />
      ))}
    </div>
  );
}

// Phase statuses derived from today vs the cycle calendar.
function phaseStates(year: number, today: string): Array<'done' | 'on' | 'up'> {
  const cy = evalCycle(year);
  const bounds = [`${year}-01-12`, `${year}-04-10`, `${year}-07-10`, cy.dAuto, cy.dMgr, cy.otoEnd, cy.close];
  return EVAL_PHASES.map((p, i) => {
    const end = bounds[i]!;
    if (today > end) return 'done';
    if (p.id === 'auto' || p.id === 'mgr') return 'on';
    if (p.id === 'oto') return today >= cy.otoStart ? 'on' : 'up';
    if (p.id === 'clo') return 'up';
    return today >= end ? 'done' : 'up';
  });
}

export function EvaluationsPage() {
  const me = useMe();
  const themeDark = useUiStore((s) => s.theme) === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();
  const today = cgToday();
  const year = Number(searchParams.get('year')) || Number(today.slice(0, 4));
  const view: View = (['campagne', 'equipe', 'objectifs'] as const).find((v) => v === searchParams.get('view')) ?? 'campagne';

  const perms = me.data?.permissions ?? [];
  const canView = perms.includes('evaluations.view');
  const canEdit = perms.includes('evaluations.edit');

  const q = useEvaluations({ year, enabled: canView });

  const [panel, setPanel] = useState<{ emp: EvalEmployeeDTO; tab: string } | null>(null);
  const [filter, setFilter] = useState<string | null>(null);
  const [mailFor, setMailFor] = useState<EvalEmployeeDTO | null>(null);
  const [printEmp, setPrintEmp] = useState<string | null>(null);
  const [confetti, setConfetti] = useState(false);

  const saveMgr = useSaveMgrEval();
  const submitMgr = useSubmitMgrEval();
  const propose = useProposeOtoSlots();
  const closeSession = useCloseEvalSession();
  const signMgr = useSignEvalMgr();
  const remind = useRemindEval();
  const createObj = useCreateObjective();
  const objAction = useObjectiveAction();

  useEffect(() => {
    if (!confetti) return;
    const id = setTimeout(() => setConfetti(false), 2200);
    return () => clearTimeout(id);
  }, [confetti]);

  useEffect(() => {
    if (!printEmp) return;
    const id = setTimeout(() => window.print(), 350);
    const done = () => setPrintEmp(null);
    window.addEventListener('afterprint', done);
    return () => { clearTimeout(id); window.removeEventListener('afterprint', done); };
  }, [printEmp]);

  const rows: EvRow[] = useMemo(
    () => (q.data?.employees ?? []).map((e) => ({ e, ev: q.data!.evaluations[e.id]! })).filter((r) => r.ev),
    [q.data],
  );

  // ASSISTANT (no evaluations.view) never sees this page.
  if (me.data && !canView) return <Navigate to="/dashboard" replace />;

  const cycle = q.data?.cycle ?? evalCycle(year);
  const daysLeft = Math.round((Date.parse(cycle.dAuto) - Date.parse(today)) / 86_400_000);
  const phases = phaseStates(year, today);
  const setParams = (patch: Record<string, string | null>) => {
    const p = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null) p.delete(k);
      else p.set(k, v);
    }
    setSearchParams(p, { replace: true });
  };
  const go = (v: View) => setParams({ view: v === 'campagne' ? null : v });

  const lateCount = rows.filter(({ ev }) => ev.auto.status !== 'SUBMITTED').length;
  const openEmp = (emp: EvalEmployeeDTO, tab: string) => setPanel({ emp, tab });

  const err = (label: string) => () => toast.danger(label);

  const doRemind = (emp: EvalEmployeeDTO) =>
    remind.mutate({ employeeId: emp.id, year }, {
      onSuccess: (r) => { setMailFor(null); toast.success(`Rappel envoyé à ${emp.name} (${r.to})`); },
      onError: err("Impossible d'envoyer le rappel."),
    });

  const remindAll = (emps: EvalEmployeeDTO[]) => {
    setMailFor(null);
    Promise.allSettled(emps.map((e) => remind.mutateAsync({ employeeId: e.id, year }))).then((rs) => {
      const ok = rs.filter((r) => r.status === 'fulfilled').length;
      if (ok) toast.success(`Rappel groupé envoyé à ${ok} employé${ok > 1 ? 's' : ''}`);
      if (ok < emps.length) toast.danger(`${emps.length - ok} rappel(s) en échec`);
    });
  };

  const otoActions = (emp: EvalEmployeeDTO) => ({
    propose: canEdit ? (slots: EvalSlot[]) => propose.mutate({ employeeId: emp.id, year, slots }, {
      onSuccess: () => toast.success(`${slots.length} créneaux envoyés à ${emp.name}`),
      onError: err("Impossible d'envoyer les créneaux."),
    }) : undefined,
    close: canEdit ? (notes: EvalNotes, cr: string) => closeSession.mutate({ employeeId: emp.id, year, notes, cr }, {
      onSuccess: () => toast.success('Consensus enregistré — fiche envoyée pour signatures'),
      onError: err("Impossible d'enregistrer le consensus."),
    }) : undefined,
    signMgr: canEdit ? () => signMgr.mutate({ employeeId: emp.id, year }, {
      onSuccess: (dto) => {
        if (dto.final.done) { toast.success('Fiche signée par les deux parties — évaluation clôturée 🎉'); setConfetti(true); }
        else toast.success("Signature enregistrée — en attente de l'autre partie");
      },
      onError: err("Impossible d'enregistrer la signature."),
    }) : undefined,
    print: () => setPrintEmp(emp.id),
  });

  const objHandlers = (emp: EvalEmployeeDTO) => ({
    onCreate: (tpl: { cat: string; title: string; kpi: string; deadline: string; description?: string }) =>
      createObj.mutate({ employeeId: emp.id, year, ...tpl }, {
        onSuccess: () => toast.success('Objectif ajouté et validé'),
        onError: err("Impossible d'ajouter l'objectif."),
      }),
    onValidate: (id: string) => objAction.mutate({ id, action: 'validate' }, {
      onSuccess: () => toast.success('Objectif validé — il entre dans le suivi trimestriel'),
      onError: err('Action impossible.'),
    }),
    onRefuse: (id: string) => objAction.mutate({ id, action: 'refuse' }, {
      onSuccess: () => toast.success('Proposition refusée'),
      onError: err('Action impossible.'),
    }),
    onCheck: (id: string, input: ObjCheckInput) => objAction.mutate({ id, action: 'check', q: input.q, prog: input.prog, status: input.status, note: input.note }, {
      onSuccess: () => toast.success(`Check-in ${input.q} enregistré`),
      onError: err('Action impossible.'),
    }),
  });

  const renderPanelTab = (emp: EvalEmployeeDTO, tab: string) => {
    const ev = q.data?.evaluations[emp.id];
    if (!ev) return null;
    const mgrName = ev.mgr.byName ?? 'Direction';
    const empFirst = emp.name.trim().split(/\s+/).at(-1) ?? emp.name;
    if (tab === 'eval') {
      return (
        <EvEvalForm key={`${emp.id}-mgr-${ev.mgr.status}`} role="mgr" tplCode={ev.template}
          notes={ev.mgr.notes ?? {}} comment={ev.mgr.comment ?? ''}
          readOnly={!canEdit || ev.mgr.status === 'SUBMITTED'} submittedAt={ev.mgr.submittedAt}
          deadline={cycle.dMgr} saving={saveMgr.isPending}
          onSave={(d) => saveMgr.mutate({ employeeId: emp.id, year, notes: d.notes, comment: d.comment })}
          onSubmit={() => submitMgr.mutate({ employeeId: emp.id, year }, {
            onSuccess: (dto) => toast.success(dto.auto.status === 'SUBMITTED'
              ? 'Évaluation soumise — la comparaison des écarts est maintenant disponible'
              : "Évaluation soumise — en attente de l'auto-évaluation"),
            onError: err('Soumission impossible.'),
          })} />
      );
    }
    if (tab === 'auto') {
      return ev.auto.status === 'SUBMITTED' && ev.auto.notes
        ? <EvEvalForm key={`${emp.id}-auto`} role="auto" tplCode={ev.template} notes={ev.auto.notes} bilan={ev.auto.bilan ?? []}
            readOnly submittedAt={ev.auto.submittedAt} deadline={cycle.dAuto} />
        : (
          <div className="ev-lock">
            <span className="ic">⏳</span>
            <h4>Auto-évaluation non soumise</h4>
            <p>
              {empFirst} n'a pas encore soumis son auto-évaluation
              ({ev.auto.status === 'DRAFT' ? `brouillon en cours — ${ev.auto.pct}% complété` : 'pas commencée'}).
              Échéance : {evFmtYmd(cycle.dAuto)}.
            </p>
          </div>
        );
    }
    if (tab === 'gap') return <EvGapView ev={ev} mgrName={mgrName} />;
    if (tab === 'oto') {
      return <EvOtoView emp={emp} ev={ev} cycle={cycle} persona="mgr" mgrName={mgrName}
        actions={otoActions(emp)} busy={propose.isPending || closeSession.isPending || signMgr.isPending} />;
    }
    if (tab === 'objectifs') {
      const h = objHandlers(emp);
      return <EvObjSection objs={q.data?.objectives[emp.id] ?? []} persona="mgr" empName={emp.name} mgrName={mgrName}
        onCreate={h.onCreate} onValidate={h.onValidate} onRefuse={h.onRefuse} onCheck={h.onCheck} />;
    }
    return null;
  };

  const printRow = printEmp ? rows.find((r) => r.e.id === printEmp) : null;

  return (
    <div className={'cgapp' + (themeDark ? ' theme-dark' : '')}>
      <div className="cg-scrollpage">
        <div className="gm-wrap">
          <div className="gm-eyebrow">Talent · {cycle.lbl}</div>
          <h1 className="gm-title">Performance <em>& objectifs</em></h1>
          <p className="gm-sub">
            Auto-évaluations, évaluations manager, écarts, entretiens 1:1 et objectifs — tout le cycle de
            performance de l'équipe, avec relances par email à chaque étape.
          </p>

          <div className="cg-ctrl">
            <div className="cg-seg">
              {([['campagne', 'Campagne'], ['equipe', 'Équipe'], ['objectifs', 'Objectifs']] as const).map(([k, l]) => (
                <button key={k} className={view === k ? 'on' : ''} onClick={() => go(k)}>
                  {l}
                  {k === 'campagne' && lateCount > 0 && <span className="bdg">{lateCount}</span>}
                </button>
              ))}
            </div>
            <div className="cg-spacer" />
            <span className="ev-due warn" style={{ fontSize: 11 }}>
              Auto-évals : échéance {evFmtYmd(cycle.dAuto)}{daysLeft >= 0 ? ` · J-${daysLeft}` : ''}
            </span>
          </div>

          <div className="ev-phasebar">
            {EVAL_PHASES.map((p, i) => (
              <div key={p.id} className={'ev-ph ' + phases[i]}>
                <span className="d">{phases[i] === 'done' ? '✓' : phases[i] === 'on' ? '●' : ''}</span>
                <span className="l">{p.lbl}</span>
                <span className="dt">{p.dt}</span>
              </div>
            ))}
          </div>

          {q.isLoading && <div className="cg-placeholder view-enter">Chargement de la campagne…</div>}
          {q.data && view === 'campagne' && (
            <EvCampagne rows={rows} cycle={cycle} today={q.data.today} reminders={q.data.reminders} daysLeft={daysLeft}
              onOpen={openEmp} onRemind={(e) => setMailFor(e)} onRemindAll={remindAll}
              onCell={(ent) => { setFilter(ent); go('equipe'); }} />
          )}
          {q.data && view === 'equipe' && (
            <EvEquipe rows={rows} filter={filter} onFilter={setFilter} onOpen={openEmp} />
          )}
          {q.data && view === 'objectifs' && (
            <div className="view-enter">
              <EvObjBoard team={q.data.employees} objectives={q.data.objectives} onOpen={openEmp} />
            </div>
          )}
        </div>
      </div>

      {panel && (<>
        <div className="ev-ov" onClick={() => setPanel(null)} />
        <div className="ev-panel">
          <div className="ev-panel-h">
            <EvEmpCell emp={panel.emp} />
            {q.data?.evaluations[panel.emp.id] && <EvStPill step={q.data.evaluations[panel.emp.id]!.step} />}
            <div className="ev-tabs" style={{ marginLeft: 'auto' }}>
              {([['eval', 'Évaluer'], ['auto', 'Auto-éval'], ['gap', 'Comparaison'], ['oto', '1:1 & consensus'], ['objectifs', 'Objectifs']] as const).map(([k, l]) => (
                <button key={k} className={panel.tab === k ? 'on' : ''} onClick={() => setPanel((p) => (p ? { ...p, tab: k } : p))}>
                  <span className="td" />{l}
                </button>
              ))}
            </div>
            <button className="sb-dr-x x" onClick={() => setPanel(null)}>✕</button>
          </div>
          <div className="ev-panel-b">{renderPanelTab(panel.emp, panel.tab)}</div>
        </div>
      </>)}

      {mailFor && (
        <EvEmailModal emp={mailFor} cycle={cycle} sending={remind.isPending}
          onClose={() => setMailFor(null)} onSend={() => doRemind(mailFor)} />
      )}
      {confetti && <EvConfetti />}
      {printRow && (
        <EvFiche emp={printRow.e} ev={printRow.ev} objs={q.data?.objectives[printRow.e.id] ?? []}
          mgrName={printRow.ev.mgr.byName ?? 'Direction'} cycle={cycle} />
      )}
    </div>
  );
}
